/**
 * AV and IFMV Utilities
 * 
 * Centralized utility functions for extracting Assessment Value (AV) and IFMV values based on villageRun flag
 * Uses getNegotiationObj villageRun flag to extract village-specific values if villageRun is true,
 * otherwise uses town-specific fields.
 */

import { usePersistedDataStore } from '../components/store';

/**
 * Extracts AV value based on villageRun flag from negotiation object.
 * @param {Object} dataObj - Object containing AV fields (e.g., case object, row object, compSheet, etc.)
 * @param {number|null} [arrayIndex=null] - Optional index for array-based AV fields (e.g., compSheet.AssessmentTown[0])
 * @returns {number|null} The AV value or null if not found
 */
export const getAVUtil = (dataObj, arrayIndex = null) => {
  if (!dataObj) return null;

  const getNegotiationObj = usePersistedDataStore.getState().negotiationObj;
  const villageRun = getNegotiationObj?.villageRun === 1 || getNegotiationObj?.villageRun === true;

  let fieldValue = null;

  if (villageRun) {
    fieldValue = dataObj.AssessmentVillage ?? dataObj.PropertyAssessment ?? null;
  } else {
    fieldValue = dataObj.AssessmentTown ?? dataObj.PropertyAssessment ?? null;
  }

  if (fieldValue === null || fieldValue === undefined) return null;

  if (Array.isArray(fieldValue) && arrayIndex !== null && arrayIndex !== undefined) {
    return fieldValue[arrayIndex] ?? null;
  }
  if (!Array.isArray(fieldValue)) {
    return fieldValue;
  }

  return null;
};

/**
 * Extracts IFMV value based on villageRun flag from negotiation object or the compsheet.
 * @param {Object} dataObj - Object containing IFMV fields (e.g., compSheet, case object, etc.)
 * @param {number} arrayIndex - Optional index for array-based IFMV fields (e.g., compSheet.IFMV[index])
 * @returns {number|null} The IFMV value or null if not found
 */
export const getIFMVUtil = (dataObj, arrayIndex = null) => {
  if (!dataObj) return null;

  const getNegotiationObj = usePersistedDataStore.getState().negotiationObj;
  const villageRun = getNegotiationObj?.villageRun === 1 || getNegotiationObj?.villageRun === true;

  let ifmvValue = null;
  let fieldValue = null;

  if (villageRun) {
    // The third fallback is so stupid but it makes the offer sending flow work for villages.
    fieldValue = dataObj.IFMV_Village ?? dataObj.IFMVVillage ?? dataObj.IFMV ?? null;
  } else {
    fieldValue = dataObj.IFMV_Town ?? dataObj.IFMVTown ?? dataObj.IFMV ?? null;
  }

  if (fieldValue !== null && fieldValue !== undefined) {
    if (Array.isArray(fieldValue) && arrayIndex !== null && arrayIndex !== undefined) {
      ifmvValue = fieldValue[arrayIndex] ?? null;
    } else if (!Array.isArray(fieldValue)) {
      ifmvValue = fieldValue;
    }
  }

  return ifmvValue;
};

